import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useBreedConstants } from "@/app/hooks/useBreedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useRef, useState } from "react";
import VaccinationInfo from "../VaccinationInfo";

interface RabiesTitrePetHistoryCollectionProps {
  source?: string;
}

const MultiImageSection = ({
  label,
  images,
  onAdd,
  onRemove,
}: {
  label: string;
  images: string[];
  onAdd: (files: FileList) => void;
  onRemove: (index: number) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasImages = images.length > 0;

  return (
    <div className="flex flex-col gap-1 pt-3 w-full">
      <p className="text-sm w-full">{label}</p>

      {!hasImages && (
        <div
          className="flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 rounded-lg p-5 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors print:hidden"
          onClick={() => inputRef.current?.click()}
        >
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 12l-4-4m0 0l-4 4m4-4v12" />
          </svg>
          <p className="text-xs font-semibold text-gray-500">Upload</p>
          <p className="text-[10px] text-gray-400">Click to upload or drag and drop</p>
        </div>
      )}

      {hasImages && (
        <div className="flex flex-wrap gap-2">
          {images.map((src, i) => (
            <div key={i} className="relative w-[200px] h-[200px] rounded overflow-hidden border border-gray-200 group">
              <img src={src} alt="" className="w-full h-full object-fit" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-0.5 right-0.5 w-4 h-4 bg-black bg-opacity-60 text-white rounded-full text-[9px] flex items-center justify-center hover:bg-opacity-90 print:hidden"
              >
                ✕
              </button>
            </div>
          ))}

          <div
            className="w-16 h-16 border border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors print:hidden"
            onClick={() => inputRef.current?.click()}
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <p className="text-[9px] text-gray-400 mt-0.5">Add More</p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) {
            onAdd(e.target.files);
            e.target.value = "";
          }
        }}
      />
    </div>
  );
};

const RabiesTitrePetHistoryCollection = ({ source }: RabiesTitrePetHistoryCollectionProps) => {
  const { getRouteKey } = useRouter();
  const { formatDate } = useUtils();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerDetails, petDetails } = routeState.state as ICustomerProfileState;
  const { catBreeds, dogBreeds, giantBreeds } = useBreedConstants();

  const [formData, setFormData] = useState({
    petName: "",
    petSpecies: "",
    petGender: "",
    petBreed: "",
    petDOB: "",
    ownerName: "",
    contactNumber: "",
    ownerAddress: "",
    date: null as string | null,
    petColor: "",
    microchipCertNo: "",
    destinationCountry: "",
    customerEmail: "",
    dateOfSampling: "",
    dateOfVaccination: "",
    vaccineManufacturerBatch: "",
  });

  const [vacImages, setVacImages] = useState<string[]>([]);
  const [micImages, setMicImages] = useState<string[]>([]);

  const addImages = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    files: FileList,
  ) => {
    const urls = Array.from(files).map((f) => URL.createObjectURL(f));
    setter((prev) => [...prev, ...urls]);
  };

  const removeImage = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    index: number,
  ) => {
    URL.revokeObjectURL(list[index]);
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const breadDetection = (petSpeciesId, petBreedId) => {
    if (petSpeciesId === "1") {
      const filteredCatBreeds = dogBreeds.filter((catBreed) => catBreed.id === petBreedId);
      return filteredCatBreeds[0]?.pet_breed;
    } else if (petSpeciesId === "2") {
      const filteredCatBreeds = catBreeds.filter((catBreed) => catBreed.id === petBreedId);
      return filteredCatBreeds[0]?.pet_breed;
    } else if (petSpeciesId === "15") {
      const filteredGiantBreeds = giantBreeds.filter((giantBreed) => giantBreed.id === petBreedId);
      return filteredGiantBreeds[0]?.pet_breed;
    } else {
      return "Other";
    }
  };

  useEffect(() => {
    if (petDetails && customerDetails) {
      setFormData((prevData) => ({
        ...prevData,
        petName: petDetails?.name,
        petSpecies: petDetails?.pet_species_type_id === "1" ? "DOG" : "CAT",
        petGender: petDetails?.gender,
        petDOB: petDetails?.dob,
        petBreed: breadDetection(petDetails?.pet_species_type_id, petDetails?.pet_breed_id),
        ownerName: customerDetails?.name,
        contactNumber: customerDetails?.mobile,
        date: formatDate(),
        petColor: "",
      }));
    }
  }, [customerDetails, petDetails]);

  const data = [
    { label: "Pet Name", key: "petName" },
    { label: "Pet Species", key: "petSpecies" },
    { label: "Pet Breed", key: "petBreed" },
    { label: "Pet Gender", key: "petGender" },
    { label: "Pet DOB", key: "petDOB" },
    { label: "Owner Name", key: "ownerName" },
    { label: "Contact Number", key: "contactNumber" },
    { label: "Date", key: "date" },
    { label: "Pet Color", key: "color" },
  ];

  return (
    <div className="mb-2">
      <div className="bg-white">

        <table className="w-full border-none flex">
          <tbody>
            {(source === "HELTH" ? data : data.slice(0, -1)).map(({ label, key }) => (
              <tr key={key}>
                <td className="font-semibold text-xs">{label}:</td>
                <td>
                  <input
                    type="text"
                    className="border w-[200px] p-1 focus:outline-none text-xs"
                    value={formData[key as keyof typeof formData] ?? ""}
                    onChange={(e) => handleInputChange(e, key)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Microchip Certificate number:</p>
          <input type="text" className="text-sm w-[400px] border p-1" />
        </div>
        <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Destination Country:</p>
          <input type="text" className="text-sm w-[400px] border p-1" />
        </div> <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Customer Email Id:</p>
          <input type="text" className="text-sm w-[400px] border p-1" />
        </div>

        <div className="mt-4">
          <VaccinationInfo />
        </div>

        <MultiImageSection
          label="Vaccination Capture image with vaccine Number:"
          images={vacImages}
          onAdd={(files) => addImages(setVacImages, files)}
          onRemove={(i) => removeImage(setVacImages, vacImages, i)}
        />

        <MultiImageSection
          label="Microchip Capture Image With Chip Number:"
          images={micImages}
          onAdd={(files) => addImages(setMicImages, files)}
          onRemove={(i) => removeImage(setMicImages, micImages, i)}
        />
        
        {/* <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Date of Sampling:</p>
          <input type="date" className="text-sm w-[400px] border p-1" onChange={(e) => handleInputChange(e, "dateOfSampling")} />
        </div>

        <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Date of Vaccination:</p>
          <input type="date" className="text-sm w-[400px] border p-1" onChange={(e) => handleInputChange(e, "dateOfVaccination")} />
        </div> */}

        <div className="gap-2 flex items-center w-full pt-3">
  <p className="text-sm w-[280px]">Date of Sampling:</p>
  <input
    type="date"
    className="text-sm w-[400px] border p-1 print:hidden"
    value={formData.dateOfSampling}
    onChange={(e) => handleInputChange(e, "dateOfSampling")}
  />
  {formData.dateOfSampling && (
    <span className="hidden print:inline text-sm">{formData.dateOfSampling}</span>
  )}
</div>

<div className="gap-2 flex items-center w-full pt-3">
  <p className="text-sm w-[280px]">Date of Vaccination:</p>
  <input
    type="date"
    className="text-sm w-[400px] border p-1 print:hidden"
    value={formData.dateOfVaccination}
    onChange={(e) => handleInputChange(e, "dateOfVaccination")}
  />
  {formData.dateOfVaccination && (
    <span className="hidden print:inline text-sm">{formData.dateOfVaccination}</span>
  )}
</div>

        <div className="gap-2 flex items-center w-full pt-3">
          <p className="text-sm w-[280px]">Vaccine Manufacturer and Batch No:</p>
          <input type="text" className="text-sm w-[400px] border p-1" />
        </div>

      </div>

      <style>{`
        @media print {
          input[type="text"],
          input[type="date"],
          input[type="email"] { border: none !important; }
          input[type="date"] { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default RabiesTitrePetHistoryCollection;
